import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText } from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';
import PageShell from './PageShell';

export type LegalSlug = 'sss' | 'terms' | 'privacy' | 'cookies' | 'copyright' | 'about';

const CONTACT_EMAIL = 'destek@allone.com';

const h2: React.CSSProperties = {
  color: 'var(--text-secondary, #e2e8f0)',
  fontSize: '1.05rem',
  marginTop: '1.75rem',
  marginBottom: '0.5rem',
};

const p: React.CSSProperties = {
  marginTop: 0,
  marginBottom: '1rem',
};

const link: React.CSSProperties = {
  color: 'var(--accent-primary)',
  textDecoration: 'none',
};

function LegalLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link to={to} style={link}>
      {children}
    </Link>
  );
}

const LegalPages: React.FC<{ page: LegalSlug }> = ({ page }) => {
  const title =
    page === 'sss'
      ? 'Sıkça Sorulan Sorular'
      : page === 'terms'
        ? 'Kullanım Şartları'
        : page === 'privacy'
          ? 'Gizlilik Politikası'
          : page === 'cookies'
            ? 'Çerez Politikası'
            : page === 'copyright'
              ? 'Telif ve İçerik Politikası'
              : page === 'about'
                ? 'Hakkımızda'
                : 'Bilgi';

  usePageTitle(title);

  return (
    <PageShell badge="Yasal" badgeIcon={FileText} title={title} maxWidth="800px">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
        <div
          className="glow-card legal-content"
          style={{
            padding: '28px 24px',
            borderRadius: 'var(--radius-card, 20px)',
            color: 'var(--text-muted, #cbd5e1)',
            lineHeight: 1.75,
            fontSize: '15px',
          }}
        >
          {page === 'sss' && <SssContent />}
          {page === 'terms' && <TermsContent />}
          {page === 'privacy' && <PrivacyContent />}
          {page === 'cookies' && <CookiesContent />}
          {page === 'copyright' && <CopyrightContent />}
          {page === 'about' && <AboutContent />}
        </div>
      </motion.div>
    </PageShell>
  );
};

function SssContent() {
  return (
    <>
      <h2 style={{ ...h2, marginTop: 0 }}>Genel</h2>
      <p style={p}>
        <strong style={{ color: 'var(--text-secondary)' }}>All One Topluluğu nedir?</strong>
        <br />
        Üniversite öğrencilerinin not ve kaynak paylaşabildiği, günlük sorulara katılabildiği ve forumda birbirine
        yardım edebildiği bağımsız bir topluluk platformudur. Resmi bir üniversite web sitesi değildir.
      </p>
      <p style={p}>
        <strong style={{ color: 'var(--text-secondary)' }}>Notları indirmek ücretli mi?</strong>
        <br />
        Hayır; onaylanmış notlara erişim ücretsizdir. Kayıt, topluluk kurallarına uymak ve indirme sayacının doğru
        işlemesi için istenebilir.
      </p>
      <p style={p}>
        <strong style={{ color: 'var(--text-secondary)' }}>Not yüklerken nelere dikkat etmeliyim?</strong>
        <br />
        Yalnızca paylaşma hakkına sahip olduğunuz içerikleri yükleyin. Ayrıntılar için{' '}
        <LegalLink to="/telif">Telif ve İçerik Politikası</LegalLink> sayfasına bakın.
      </p>
      <p style={p}>
        <strong style={{ color: 'var(--text-secondary)' }}>Forum görselleri nerede tutuluyor?</strong>
        <br />
        Görseller imgbb gibi harici bir görsel servisine yüklenir; sistemimizde yalnızca bağlantı (URL) saklanır.
      </p>
    </>
  );
}

function TermsContent() {
  return (
    <>
      <p style={{ ...p, marginTop: 0 }}>
        Bu kullanım şartları, All One Topluluğu web sitesini ve ilgili hizmetleri kullanımınızı düzenler. Siteyi
        kullanarak veya hesap oluşturarak bu şartları kabul etmiş sayılırsınız.
      </p>
      <h2 style={h2}>Hesap ve yaşı</h2>
      <p style={p}>
        Kayıt sırasında doğru bilgi vermekle yükümlüsünüz. Hesabınızın güvenliğinden siz sorumlusunuz; şifrenizi
        üçüncü kişilerle paylaşmayın.
      </p>
      <h2 style={h2}>İçerik kuralları</h2>
      <p style={p}>
        Paylaştığınız not, yorum ve forum içeriklerinin telif ve kullanım haklarından birinci derecede siz
        sorumlusunuz. Başkalarına ait materyalleri izinsiz paylaşmamalısınız. Telif ihlali, hakaret, spam veya
        yasa dışı içerik yasaktır. Ayrıntılı çerçeve için{' '}
        <LegalLink to="/telif">Telif ve İçerik Politikası</LegalLink> geçerlidir.
      </p>
      <h2 style={h2}>Moderasyon</h2>
      <p style={p}>
        Yönetici ekibi, kurallara aykırı içerikleri kaldırma, notları reddetme veya hesabı geçici/kalıcı olarak
        askıya alma hakkını saklı tutar.
      </p>
      <h2 style={h2}>Hizmetin değiştirilmesi</h2>
      <p style={p}>
        Platform özellikleri zaman içinde güncellenebilir. Mümkün olduğunca kesintisiz hizmet hedeflenir; buna karşın
        teknik bakım veya zorunlu değişiklikler için önceden haber verilemeyebilir.
      </p>
      <h2 style={h2}>İletişim</h2>
      <p style={p}>
        Sorularınız için: <a href={`mailto:${CONTACT_EMAIL}`} style={link}>{CONTACT_EMAIL}</a>
      </p>
    </>
  );
}

function PrivacyContent() {
  return (
    <>
      <p style={{ ...p, marginTop: 0 }}>
        Gizliliğinize saygı duyuyoruz. Bu metin, 6698 sayılı Kişisel Verilerin Korunması Kanunu (KVKK) kapsamında
        hangi verilerin işlenebileceğini ve hangi amaçlarla kullanıldığını özetler.
      </p>
      <h2 style={h2}>Veri sorumlusu</h2>
      <p style={p}>
        Veri sorumlusu: <strong style={{ color: 'var(--text-secondary)' }}>All One Topluluğu</strong> (bireysel
        işletmeci).
        <br />
        İletişim: <a href={`mailto:${CONTACT_EMAIL}`} style={link}>{CONTACT_EMAIL}</a>
      </p>
      <h2 style={h2}>İşlenen kişisel veriler</h2>
      <ul style={{ paddingLeft: '1.25rem', marginBottom: '1rem' }}>
        <li>Hesap bilgileri: ad, e-posta, şifre (hashlenmiş)</li>
        <li>Oturum ve güvenlik: cihaz tanımlayıcısı, IP tabanlı güvenlik kayıtları</li>
        <li>Platform kullanımı: yüklenen not meta verileri, indirme/puanlama, forum içerikleri</li>
        <li>Tercihler: tema seçimi, bildirim durumu</li>
      </ul>
      <h2 style={h2}>İşleme amaçları</h2>
      <p style={p}>
        Üyelik ve kimlik doğrulama, not paylaşımı ve forum hizmetlerinin sunulması, içerik moderasyonu, kötüye
        kullanımın önlenmesi (ban/cihaz engeli), teknik destek ve yasal yükümlülüklerin yerine getirilmesi.
      </p>
      <h2 style={h2}>Hukuki sebep</h2>
      <p style={p}>
        Verileriniz; sözleşmenin kurulması ve ifası, veri sorumlusunun meşru menfaati ve açık rızanız (çerezler
        gibi) kapsamında işlenebilir.
      </p>
      <h2 style={h2}>Saklama süresi</h2>
      <p style={p}>
        Veriler, hizmetin sunulması için gerekli süre boyunca ve yasal yükümlülükler saklı kalmak kaydıyla
        saklanır. Hesabınızı silme talebinde bulunabilirsiniz.
      </p>
      <h2 style={h2}>Üçüncü taraf hizmetler</h2>
      <p style={p}>
        Barındırma (Vercel), veritabanı (MongoDB Atlas), dosya depolama (Google Drive), görsel barındırma (imgbb),
        e-posta (Resend) gibi hizmet sağlayıcılar yalnızca hizmetin sunulması amacıyla veri işleyebilir.
      </p>
      <h2 style={h2}>Haklarınız (KVKK md. 11)</h2>
      <p style={p}>
        Kişisel verilerinizin işlenip işlenmediğini öğrenme, düzeltilmesini veya silinmesini isteme, işleme
        faaliyetlerine itiraz etme haklarına sahipsiniz. Taleplerinizi{' '}
        <a href={`mailto:${CONTACT_EMAIL}`} style={link}>{CONTACT_EMAIL}</a> adresine iletebilirsiniz.
      </p>
      <h2 style={h2}>Çerezler</h2>
      <p style={p}>
        Oturumunuzun açık kalması ve site deneyiminin iyileştirilmesi için çerezler kullanılabilir. Ayrıntılar için{' '}
        <LegalLink to="/cookies">Çerez Politikası</LegalLink> sayfasına bakın.
      </p>
    </>
  );
}

function CopyrightContent() {
  return (
    <>
      <p style={{ ...p, marginTop: 0 }}>
        Bu metin, All One Topluluğu&apos;nda paylaşılan ders notları ve kullanıcı içerikleriyle ilgili telif
        sorumluluğu ve bildirim süreçlerini açıklar. <em>Hukuki danışmanlık yerine geçmez.</em>
      </p>
      <h2 style={h2}>Platformun rolü</h2>
      <p style={p}>
        All One Topluluğu, kullanıcıların içerik yüklediği bir aracı/barındırıcı platformdur. 5846 sayılı Fikir ve
        Sanat Eserleri Kanunu uyarınca eser üzerindeki mali haklar, eser sahibine veya hak sahibine aittir.
        Platform, kullanıcılar adına içerik üretmez; yalnızca paylaşım altyapısı sağlar.
      </p>
      <h2 style={h2}>Kullanıcı yükümlülüğü</h2>
      <p style={p}>
        Yüklediğiniz her içerik için paylaşma yetkisine sahip olduğunuzu beyan etmiş olursunuz. Kendi el yazınız
        notlar, açık izin verilen kaynaklar ve eğitim amaçlı paylaşıma uygun materyaller kabul edilebilir. İzinsiz
        yayınevi kitap taramaları, kapalı slayt/past exam paketleri veya telif hakkı ihlali oluşturan materyaller
        yasaktır.
      </p>
      <h2 style={h2}>Sorumluluk sınırı</h2>
      <p style={p}>
        İçeriklerden birinci derecede yükleyen kullanıcı sorumludur. Platform, makul ölçüde moderasyon ve ihbar
        incelemesi yapar; ancak tüm ihlalleri önceden tespit edeceğini garanti etmez.
      </p>
      <h2 style={h2}>Telif ihbarı ve kaldırma</h2>
      <p style={p}>
        Telif veya hak ihlali iddiasında bulunmak için{' '}
        <a href={`mailto:${CONTACT_EMAIL}`} style={link}>{CONTACT_EMAIL}</a> adresine şu bilgileri iletin:
      </p>
      <ul style={{ paddingLeft: '1.25rem', marginBottom: '1rem' }}>
        <li>İhlal edildiği iddia edilen eserin tanımı</li>
        <li>Platformdaki içeriğin bağlantısı veya tanımlayıcı bilgisi</li>
        <li>İletişim bilgileriniz ve iyi niyetle yapıldığına dair kısa açıklama</li>
      </ul>
      <p style={p}>
        Geçerli bulunan taleplerde içerik kaldırılabilir veya erişime kapatılabilir. Tekrarlayan ihlallerde hesap
        askıya alınabilir veya kalıcı olarak engellenebilir (e-posta/cihaz bazlı ban).
      </p>
      <h2 style={h2}>Karşı bildirim</h2>
      <p style={p}>
        İçeriğiniz haksız yere kaldırıldığını düşünüyorsanız aynı e-posta adresine gerekçenizle başvurabilirsiniz;
        uygun görülürse içerik yeniden değerlendirilir.
      </p>
    </>
  );
}

function AboutContent() {
  return (
    <>
      <p style={{ ...p, marginTop: 0 }}>
        <strong style={{ color: 'var(--text-secondary)' }}>All One Topluluğu</strong>, üniversite öğrencilerinin
        ders notlarını paylaşabildiği, forumda soru sorabildiği ve günlük sorularla pratik yapabildiği bağımsız bir
        topluluk platformudur.
      </p>
      <h2 style={h2}>Resmi olmayan platform</h2>
      <p style={p}>
        Bu site Fırat Üniversitesi veya herhangi bir resmi kurumun web sitesi değildir. Üniversite adı yalnızca
        topluluk bağlamında anılabilir; resmi temsil iddiası taşımaz.
      </p>
      <h2 style={h2}>Misyonumuz</h2>
      <p style={p}>
        Öğrenciler arasında ücretsiz, erişilebilir ve güvenli bir not paylaşım kültürü oluşturmak; kaliteli
        içeriği moderasyon ve topluluk geri bildirimiyle desteklemek.
      </p>
      <h2 style={h2}>Nasıl çalışır?</h2>
      <ol style={{ paddingLeft: '1.25rem', marginBottom: '1rem' }}>
        <li>PDF notunuzu yükleyin.</li>
        <li>Admin ekibi içeriği inceler ve onaylar.</li>
        <li>Onaylanan notlar sitede yayınlanır; indirme sonrası puanlama ile geri bildirim toplanır.</li>
      </ol>
      <h2 style={h2}>İletişim</h2>
      <p style={p}>
        Soru, öneri ve telif bildirimleri:{' '}
        <a href={`mailto:${CONTACT_EMAIL}`} style={link}>{CONTACT_EMAIL}</a>
      </p>
    </>
  );
}

function CookiesContent() {
  return (
    <>
      <p style={{ ...p, marginTop: 0 }}>
        Çerezler, ziyaret ettiğiniz web sitesi tarafından tarayıcınıza kaydedilen küçük metin dosyalarıdır.
      </p>
      <h2 style={h2}>Neden kullanıyoruz?</h2>
      <p style={p}>
        Oturum açma bilgisinin (ör. JWT) güvenli şekilde tutulması, tercihlerinizi hatırlamak (tema, çerez onayı) ve
        site performansını ölçmek için çerezler veya benzeri teknolojiler (localStorage) kullanılabilir.
      </p>
      <h2 style={h2}>Yönetim</h2>
      <p style={p}>
        Tarayıcı ayarlarından çerezleri silebilir veya engelleyebilirsiniz. Zorunlu oturum çerezlerini kapatmanız,
        giriş yapılmış özelliklerin çalışmamasına yol açabilir.
      </p>
      <p style={p}>
        Kişisel verilerinizin işlenmesi hakkında: <LegalLink to="/privacy">Gizlilik Politikası</LegalLink>
      </p>
    </>
  );
}

export default LegalPages;
